import { onMessage, NotificationPayload } from 'firebase/messaging';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useHistory } from 'react-router';
import { useAuth } from '@data/store/AuthProvider';
import { useCompany } from '@data/store/CompanyProvider';
import { useInternationalization } from '@src/@data/store/InternationalizationProvider';
import { Empty } from '@src/@data/utility/NotImplemented';
import {
  hasNotificationPermission,
  hasAskedPermission,
  requestNotificationPermission,
  saveAskedPermission,
  registerToken,
  parseDeepLinkToPortalLink,
} from './utils';
import { logger, logOnError } from '@data/utility/errors';
import {
  getFirebaseMessaging,
  NOTIFICATION_NOT_SUPPORTED_LOG_KEY,
} from '@src/@data/utility/firebase';

interface NotificationsContextType {
  shouldShowPermission: boolean;
  requestPermission: () => Promise<void>;
  notificationsCounter: number;
  saveAskedPermission: () => void;
}

const NotificationsContext = createContext<NotificationsContextType>({
  shouldShowPermission: false,
  requestPermission: () => Promise.resolve(),
  notificationsCounter: 0,
  saveAskedPermission: Empty,
});

export const useNotifications = () => useContext(NotificationsContext);

export const NotificationsProvider: React.FC = ({ children }) => {
  const { language } = useInternationalization();
  const {
    user,
    notificationToken,
    setNotificationToken,
    isLoading: isAuthLoading,
  } = useAuth();
  const history = useHistory();
  const { selectedCompanyId } = useCompany();
  const [notificationsCounter, setNotificationsCounter] = useState(0);
  const [hasPermission, setHasPermission] = useState(
    hasNotificationPermission()
  );

  const messaging = getFirebaseMessaging();
  const shouldShowPermission =
    !!messaging &&
    !!user &&
    !!selectedCompanyId &&
    !hasPermission &&
    !hasAskedPermission();

  const requestPermission = useCallback(async () => {
    const updatedHasPermission = await requestNotificationPermission();
    setHasPermission(updatedHasPermission);
    saveAskedPermission();
  }, []);

  const showNotification = useCallback(
    (props?: NotificationPayload, data?: { deepLink?: string }) => {
      if (!props) {
        return;
      }
      try {
        const { title, ...options } = props;
        const notification = new Notification(title || '', options);
        const link = parseDeepLinkToPortalLink(data?.deepLink);

        if (link) {
          notification.onclick = () => {
            notification.close();
            history.push(link);
          };
        }
      } catch (error) {
        logger.errorOnce(
          'Browser does not support notifications',
          NOTIFICATION_NOT_SUPPORTED_LOG_KEY
        );
      }
    },
    [history]
  );

  useEffect(() => {
    if (
      !hasPermission ||
      !messaging ||
      !user ||
      !selectedCompanyId ||
      notificationToken ||
      isAuthLoading
    ) {
      return;
    }
    onMessage(messaging, ({ notification, data }) => {
      showNotification(notification, data);
      setNotificationsCounter((prev) => prev + 1);
    });

    registerToken(messaging).then(({ token, error }) => {
      if (error || !token) {
        logOnError(`Notifications: ${error ?? 'No token received'}`);
      }
      if (token) {
        setNotificationToken(token);
      }
    });
    // Updates token on locale change
  }, [
    hasPermission,
    selectedCompanyId,
    showNotification,
    user,
    language,
    notificationToken,
    setNotificationToken,
    messaging,
    isAuthLoading,
  ]);

  return (
    <NotificationsContext.Provider
      value={{
        shouldShowPermission,
        requestPermission,
        saveAskedPermission,
        notificationsCounter,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
